import { useState } from 'react';

type ViewType =
  | 'dashboard'
  | 'game-selector'
  | 'ludo'
  | 'naija-ludo'
  | 'game-setup';
type GameType = 'classic-ludo' | 'naija-ludo' | 'checkers';

export const useGameNavigation = () => {
  const [currentView, setCurrentView] = useState<
    | 'dashboard'
    | 'game-selector'
    | 'game-setup'
    | 'ludo'
    | 'naija-ludo'
    | 'checkers'
  >('dashboard');
  const [selectedGameType, setSelectedGameType] = useState<
    'classic-ludo' | 'naija-ludo' | 'checkers'
  >('classic-ludo');
  const [currentGameId, setCurrentGameId] = useState<string | null>(null);

  // Handle game selection from dashboard
  const handlePlayGames = () => {
    setCurrentView('game-selector');
  };

  // Handle game selection from game selector
  const handleGameSelect = (gameId: string) => {
    switch (gameId) {
      case 'classic-ludo':
        setSelectedGameType('classic-ludo');
        setCurrentView('game-setup');
        break;
      case 'naija-ludo':
        setSelectedGameType('naija-ludo');
        setCurrentView('game-setup');
        break;
      case 'checkers':
        setSelectedGameType('checkers');
        setCurrentView('game-setup');
        break;
      default:
        console.log(`Unknown game selected: ${gameId}`);
    }
  };

  // Navigation handlers
  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
  };

  const handleBackToGameSelector = () => {
    setCurrentView('game-selector');
  };

  const handleBackFromGameSetup = () => {
    setCurrentView('game-selector');
  };

  // Quick Play handler
  const handleQuickPlay = async (
    addDebugLog: (message: string) => void,
    setError: (error: string | null) => void
  ) => {
    addDebugLog(`Starting Quick Play for ${selectedGameType}`);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `https://koja-classics.com/api/matchmaking/quick-play`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            gameType: selectedGameType,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Quick Play failed: ${response.status}`);
      }

      const data = await response.json();
      addDebugLog(
        `Quick Play success: ${data.isNewRoom ? 'Created new room' : 'Joined existing room'} - ${data.gameId}`
      );

      // Navigate to the appropriate game
      if (selectedGameType === 'classic-ludo') {
        setCurrentView('ludo');
      } else if (selectedGameType === 'naija-ludo') {
        setCurrentView('naija-ludo');
      }
      // TODO: Add checkers view when implemented
    } catch (error) {
      console.error('Quick Play error:', error);
      addDebugLog(`Quick Play error: ${error}`);
      setError('Failed to find game. Please try again.');
    }
  };

  // Computer Game handler
  const handleComputerGame = async (
    difficulty: 'easy' | 'medium' | 'hard',
    addDebugLog: (message: string) => void,
    setError: (error: string | null) => void
  ) => {
    addDebugLog(
      `Starting AI game: ${selectedGameType} - ${difficulty} difficulty`
    );

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `https://koja-classics.com/api/matchmaking/create-ai-game`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            gameType: selectedGameType,
            difficulty: difficulty,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`AI Game creation failed: ${response.status}`);
      }

      const data = await response.json();
      addDebugLog(
        `AI Game created successfully: ${data.gameId} - ${difficulty} difficulty`
      );

      // Store the game ID for direct navigation
      setCurrentGameId(data.gameId);

      // Navigate to the appropriate game
      if (selectedGameType === 'classic-ludo') {
        setCurrentView('ludo');
      } else if (selectedGameType === 'naija-ludo') {
        setCurrentView('naija-ludo');
      }
      // TODO: Add checkers view when implemented
    } catch (error) {
      console.error('Computer Game error:', error);
      addDebugLog(`Computer Game error: ${error}`);
      setError('Failed to create AI game. Please try again.');
    }
  };

  return {
    // State
    currentView,
    selectedGameType,
    currentGameId,

    // Navigation handlers
    handlePlayGames,
    handleGameSelect,
    handleBackToDashboard,
    handleBackToGameSelector,
    handleBackFromGameSetup,

    // Game actions
    handleQuickPlay,
    handleComputerGame,
  };
};
